import { RequestDraftStatus } from '@prisma/client';
import { createContext, ReactNode, useEffect } from 'react';

import { Draft } from '../components/RequestDrafts/types';
import { useDeleteDraft } from '../hooks/requestDraft/useDeleteDraft';
import { useDiscardDraft } from '../hooks/requestDraft/useDiscardDraft';
import { useRequestDraft } from '../hooks/requestDraft/useRequestDraft';

type Context = {
  draft?: Draft;
  isPending: boolean;
  isBookingConfirmation: boolean;
  isUnsupportedRoute: boolean;

  isDiscarding: boolean;
  isDeleting: boolean;
  discardDraft: (reason: string) => Promise<void>;
  deleteDraft: () => Promise<void>;
};

export const CurrentDraftContext = createContext({} as Context);

type Props = {
  draftId: number | null;
  onResolved: () => void;
  /**
   * The draft's status, once loaded. The list shows one tab at a time and follows a draft opened from a link — a
   * quote's, the requests board's badge, a bookmark — to its own tab, still selected.
   */
  onStatusChange: (status: RequestDraftStatus) => void;
  children: ReactNode;
};

/**
 * Owns everything stateful about the draft under review: the query and the discard/delete flows. Components in
 * components/RequestDrafts are purely visual and read from this context.
 */
export const CurrentDraftProvider = ({ draftId, onResolved, onStatusChange, children }: Props) => {
  const { draft, isFetching, isPending, isBookingConfirmation, isUnsupportedRoute } = useRequestDraft(draftId);

  const [discardDraft, isDiscarding] = useDiscardDraft(draft, onResolved);
  const [deleteDraft, isDeleting] = useDeleteDraft(draft, onResolved);

  const status = draft?.status;
  useEffect(() => {
    // The cached copy shows first and can be stale — a draft discarded or applied since it was last read — so the tab
    // follows only a read that has landed, or it would jump to the old tab and back.
    if (status && !isFetching) onStatusChange(status);
  }, [status, isFetching, onStatusChange]);

  return (
    <CurrentDraftContext.Provider
      value={{
        draft,
        isPending,
        isBookingConfirmation,
        isUnsupportedRoute,
        isDiscarding,
        isDeleting,
        discardDraft,
        deleteDraft,
      }}
    >
      {children}
    </CurrentDraftContext.Provider>
  );
};
